import { Outlet, useMatches, useRouter } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { sectionPath, settingsSectionsFrom } from '../../app/nav';
import { useSession } from '../../app/session-context';
import { PageHeader } from '../../ui/PageHeader';
import { Stack } from '../../ui/Stack';
import { useDocumentTitle } from '../../useDocumentTitle';

const SUBNAV_LINK =
  'px-3 py-1 rounded-control text-md font-ui no-underline text-muted hover:bg-panel hover:text-ink current:font-semibold current:text-ink current:bg-panel focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';

/** Settings: a sub-navigation derived from the section routes' staticData, and the matched page beside it. */
export function SettingsLayout(): ReactElement {
  const router = useRouter();
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const sections = settingsSectionsFrom(Object.values(router.routesById));
  const current = useMatches().at(-1)?.routeId;
  useDocumentTitle(sections.find((s) => s.id === current)?.label);
  return (
    <div className="p-6 max-w-5xl">
      <Stack gap={6}>
        <PageHeader title="Settings" />
        <div className="flex gap-6">
          <nav aria-label="Settings" className="flex flex-col gap-1 shrink-0">
            {sections.map((s) => (
              <Link key={s.id} className={SUBNAV_LINK} href={sectionPath(s, workspaceId)} aria-current={s.id === current ? 'page' : undefined}>
                {s.label}
              </Link>
            ))}
          </nav>
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </Stack>
    </div>
  );
}
